import { ServiceType } from '@/types/bookingFlow';

export interface BookingAddOnDefinition {
  id: string;
  name: string;
  price: number;
  unit: string;
  hasQuantity: boolean;
  svgPath: string;
  applicableServices?: ServiceType[];
  excludedServices?: ServiceType[];
  pricePerSquareMeter?: boolean;
  availableOnPricingPage?: boolean;
}

const ADD_ONS_CATALOG: BookingAddOnDefinition[] = [
  {
    id: 'vacuum_cleaner',
    name: 'Vacuum cleaner',
    price: 9.0,
    unit: 'per visit',
    hasQuantity: false,
    svgPath: '/images/SVG/vacuum.svg',
  },
  {
    id: 'oven_cleaning',
    name: 'Oven cleaning',
    price: 18.0,
    unit: 'per oven',
    hasQuantity: false,
    svgPath: '/images/SVG/oven.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
    availableOnPricingPage: true,
  },
  {
    id: 'cooker_hood_cleaning',
    name: 'Cooker hood cleaning',
    price: 14.0,
    unit: 'per hood',
    hasQuantity: false,
    svgPath: '/images/SVG/cooker-hood.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'kitchen_cabinet_cleaning',
    name: 'Kitchen cabinet cleaning',
    price: 22.0,
    unit: 'per kitchen',
    hasQuantity: false,
    svgPath: '/images/SVG/kitchen cabinet.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
    availableOnPricingPage: true,
  },
  {
    id: 'dishwashing',
    name: 'Dishwashing',
    price: 9.0,
    unit: 'per session',
    hasQuantity: false,
    svgPath: '/images/SVG/dishes.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'microwave_cleaning',
    name: 'Microwave cleaning',
    price: 8.0,
    unit: 'per appliance',
    hasQuantity: false,
    svgPath: '/images/SVG/microwave.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
    availableOnPricingPage: true,
  },
  {
    id: 'ironing',
    name: 'Ironing service',
    price: 22.0,
    unit: 'per hour',
    hasQuantity: true,
    svgPath: '/images/SVG/ironing.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'pet_toilet_cleaning',
    name: 'Pet toilet cleaning',
    price: 3.5,
    unit: 'per toilet',
    hasQuantity: false,
    svgPath: '/images/SVG/pet toilet.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'wardrobe_cleaning',
    name: 'Wardrobe cleaning',
    price: 11.0,
    unit: 'per wardrobe',
    hasQuantity: true,
    svgPath: '/images/SVG/wardrobe.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'refrigerator_cleaning',
    name: 'Fridge cleaning',
    price: 13.0,
    unit: 'per fridge',
    hasQuantity: true,
    svgPath: '/images/SVG/fridge.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
    availableOnPricingPage: true,
  },
  {
    id: 'balcony_cleaning',
    name: 'Balcony cleaning',
    price: 13.0,
    unit: 'per balcony',
    hasQuantity: true,
    svgPath: '/images/SVG/balcony.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
    availableOnPricingPage: true,
  },
  {
    id: 'window_cleaning',
    name: 'Window cleaning',
    price: 13.0,
    unit: 'per window (both sides)',
    hasQuantity: true,
    svgPath: '/images/SVG/window.svg',
    applicableServices: [
      ServiceType.HOME_CLEANING,
      ServiceType.OFFICE_CLEANING,
      ServiceType.BUSINESS_CLEANING,
      ServiceType.MOVE_IN_OUT,
      ServiceType.POST_CONSTRUCTION,
    ],
    availableOnPricingPage: true,
  },
  {
    id: 'sofa_two_seater',
    name: 'Two-seater sofa cleaning',
    price: 30.0,
    unit: 'per sofa',
    hasQuantity: false,
    svgPath: '/images/SVG/sofa.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'sofa_three_seater',
    name: 'Three-seater sofa cleaning',
    price: 45.0,
    unit: 'per sofa',
    hasQuantity: false,
    svgPath: '/images/SVG/sofa.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'sofa_corner_four',
    name: 'Corner sofa (4 persons)',
    price: 60.0,
    unit: 'per sofa',
    hasQuantity: false,
    svgPath: '/images/SVG/sofa.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'sofa_corner_six',
    name: 'Corner sofa (6 persons)',
    price: 90.0,
    unit: 'per sofa',
    hasQuantity: false,
    svgPath: '/images/SVG/sofa.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'sofa_corner_large',
    name: 'Large corner sofa',
    price: 105.0,
    unit: 'per sofa',
    hasQuantity: false,
    svgPath: '/images/SVG/sofa.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'mattress_single',
    name: 'Single mattress (per side)',
    price: 20.0,
    unit: 'per mattress side',
    hasQuantity: false,
    svgPath: '/images/SVG/homecleaning.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'mattress_single_both',
    name: 'Single mattress (both sides)',
    price: 30.0,
    unit: 'per mattress',
    hasQuantity: false,
    svgPath: '/images/SVG/homecleaning.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'mattress_double',
    name: 'Double mattress (per side)',
    price: 30.0,
    unit: 'per mattress side',
    hasQuantity: false,
    svgPath: '/images/SVG/homecleaning.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'mattress_double_both',
    name: 'Double mattress (both sides)',
    price: 40.0,
    unit: 'per mattress',
    hasQuantity: false,
    svgPath: '/images/SVG/homecleaning.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'carpet',
    name: 'Carpet cleaning',
    price: 3.5,
    unit: 'per m²',
    hasQuantity: true,
    svgPath: '/images/SVG/carpet.svg',
    pricePerSquareMeter: true,
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
    availableOnPricingPage: true,
  },
  {
    id: 'carpet_floor',
    name: 'Carpet floor cleaning',
    price: 2.75,
    unit: 'per m²',
    hasQuantity: true,
    svgPath: '/images/SVG/carpet.svg',
    pricePerSquareMeter: true,
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'fitted_carpet',
    name: 'Fitted carpet cleaning',
    price: 3.4,
    unit: 'per m²',
    hasQuantity: true,
    svgPath: '/images/SVG/carpet.svg',
    pricePerSquareMeter: true,
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'chairs_stools',
    name: 'Chairs and stools cleaning',
    price: 5.0,
    unit: 'per item',
    hasQuantity: true,
    svgPath: '/images/SVG/chair.svg',
  },
  {
    id: 'chair',
    name: 'Chair cleaning',
    price: 10.0,
    unit: 'per item',
    hasQuantity: true,
    svgPath: '/images/SVG/chair.svg',
  },
  {
    id: 'office_chair',
    name: 'Office chair cleaning',
    price: 14.0,
    unit: 'per chair',
    hasQuantity: true,
    svgPath: '/images/SVG/chair.svg',
  },
  {
    id: 'upholstered_headboard',
    name: 'Upholstered headboard cleaning',
    price: 10.0,
    unit: 'per headboard',
    hasQuantity: false,
    svgPath: '/images/SVG/sofa.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'baby_stroller',
    name: 'Baby stroller cleaning',
    price: 42.0,
    unit: 'per stroller',
    hasQuantity: false,
    svgPath: '/images/SVG/homecleaning.svg',
    excludedServices: [ServiceType.OFFICE_CLEANING, ServiceType.BUSINESS_CLEANING],
  },
  {
    id: 'additional_hours',
    name: 'Additional service hour',
    price: 22.0,
    unit: 'per hour',
    hasQuantity: true,
    svgPath: '/images/SVG/cash.svg',
  },
];

export const BOOKING_ADD_ONS = ADD_ONS_CATALOG;
export const PRICING_PAGE_ADD_ONS = ADD_ONS_CATALOG.filter(addOn => addOn.availableOnPricingPage);
export const ADD_ONS_PRICING = Object.fromEntries(
  ADD_ONS_CATALOG.map(addOn => [addOn.id, addOn.price]),
);
